import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Envelope } from '@business-assistant/shared';
import type {
  UserProfile,
  TeamMember,
  TeamMemberRole,
  ModulePermissions,
  AppearanceSettings,
  NotificationPreferences,
  EmailIntegration,
  CalendarIntegration,
  StorageIntegration,
  AgencyConfiguration,
  AISettings,
  SecuritySettings,
  Theme,
  LayoutDensity,
  SidebarBehavior,
  AgencySize,
} from '../api';
import * as api from '../api';

/**
 * Settings State Interface
 *
 * Manages app-wide configuration and preferences.
 */
export interface SettingsState {
  // Data
  profile: UserProfile | null;
  teamMembers: TeamMember[];
  appearanceSettings: AppearanceSettings;
  notificationPreferences: NotificationPreferences;
  emailIntegrations: EmailIntegration[];
  calendarIntegrations: CalendarIntegration[];
  storageIntegrations: StorageIntegration[];
  agencyConfig: AgencyConfiguration | null;
  aiSettings: AISettings | null;
  securitySettings: SecuritySettings | null;

  // UI state
  loading: boolean;
  error: string | null;

  // Profile actions
  loadProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;

  // Team actions
  loadTeamMembers: () => Promise<void>;
  inviteTeamMember: (data: { email: string; name: string; role: TeamMemberRole; permissions: ModulePermissions }) => Promise<TeamMember | null>;
  updateTeamMember: (id: string, updates: Partial<TeamMember>) => Promise<void>;
  removeTeamMember: (id: string) => Promise<void>;

  // Appearance actions
  loadAppearanceSettings: () => Promise<void>;
  updateTheme: (theme: Theme) => Promise<void>;
  updateLayoutDensity: (density: LayoutDensity) => Promise<void>;
  updateSidebarBehavior: (behavior: SidebarBehavior) => Promise<void>;

  // Notification actions
  loadNotificationPreferences: () => Promise<void>;
  updateNotificationPreferences: (updates: Partial<NotificationPreferences>) => Promise<void>;

  // Integration actions
  loadIntegrations: () => Promise<void>;
  connectEmailIntegration: (provider: 'gmail' | 'outlook', email: string) => Promise<EmailIntegration | null>;
  disconnectEmailIntegration: (id: string) => Promise<void>;
  connectCalendarIntegration: (provider: 'google' | 'outlook', email: string) => Promise<CalendarIntegration | null>;
  disconnectCalendarIntegration: (id: string) => Promise<void>;
  connectStorageIntegration: (provider: 'google_drive' | 'dropbox' | 'onedrive', email: string) => Promise<StorageIntegration | null>;
  disconnectStorageIntegration: (id: string) => Promise<void>;

  // Agency actions
  loadAgencyConfig: () => Promise<void>;
  updateAgencyConfig: (updates: Partial<AgencyConfiguration>) => Promise<void>;

  // AI actions
  loadAISettings: () => Promise<void>;
  updateAISettings: (updates: Partial<AISettings>) => Promise<void>;

  // Security actions
  loadSecuritySettings: () => Promise<void>;
  updateSecuritySettings: (updates: Partial<SecuritySettings>) => Promise<void>;

  // Utility actions
  loadAllSettings: () => Promise<void>;
  clearAllData: () => void;
  seedSampleData: () => Promise<void>;
  clearError: () => void;
}

/**
 * Settings Zustand Store
 *
 * Manages settings state with localStorage persistence for UI preferences.
 */
export const useSettingsStore = create<SettingsState>()(
  persist(
    immer((set, get) => ({
      // Initial data
      profile: null,
      teamMembers: [],
      appearanceSettings: {
        theme: 'auto',
        layoutDensity: 'comfortable',
        sidebarBehavior: 'pinned',
      },
      notificationPreferences: {
        emailNotifications: true,
        pushNotifications: false,
        notificationTypes: {
          task_assigned: true,
          due_date_upcoming: true,
          new_message: true,
          new_call: true,
          invoice_due: true,
          invoice_overdue: true,
        },
      },
      emailIntegrations: [],
      calendarIntegrations: [],
      storageIntegrations: [],
      agencyConfig: null,
      aiSettings: null,
      securitySettings: null,

      // Initial UI state
      loading: false,
      error: null,

      // Profile actions
      loadProfile: async () => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.getUserProfile();

          if (result.success && result.data) {
            set((state) => {
              state.profile = result.data!;
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to load profile';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error loading profile';
            state.loading = false;
          });
        }
      },

      updateProfile: async (updates: Partial<UserProfile>) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.updateUserProfile(updates);

          if (result.success) {
            set((state) => {
              if (state.profile) {
                state.profile = { ...state.profile, ...updates };
              }
              state.loading = false;
            });
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to update profile';
              state.loading = false;
            });
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error updating profile';
            state.loading = false;
          });
        }
      },

      // Team actions
      loadTeamMembers: async () => {
        try {
          const result = await api.listTeamMembers();

          if (result.success && result.data) {
            set((state) => {
              state.teamMembers = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load team members:', err);
        }
      },

      inviteTeamMember: async (data) => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          const result = await api.inviteTeamMember(data);

          if (result.success && result.data) {
            set((state) => {
              state.teamMembers.push(result.data!);
              state.loading = false;
            });
            return result.data;
          } else {
            set((state) => {
              state.error = result.error?.message || 'Failed to invite team member';
              state.loading = false;
            });
            return null;
          }
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error inviting team member';
            state.loading = false;
          });
          return null;
        }
      },

      updateTeamMember: async (id: string, updates: Partial<TeamMember>) => {
        try {
          const result = await api.updateTeamMember(id, updates);

          if (result.success) {
            set((state) => {
              const index = state.teamMembers.findIndex((m) => m.id === id);
              if (index !== -1) {
                state.teamMembers[index] = { ...state.teamMembers[index], ...updates };
              }
            });
          }
        } catch (err) {
          console.error('Failed to update team member:', err);
        }
      },

      removeTeamMember: async (id: string) => {
        try {
          const result = await api.removeTeamMember(id);

          if (result.success) {
            set((state) => {
              state.teamMembers = state.teamMembers.filter((m) => m.id !== id);
            });
          }
        } catch (err) {
          console.error('Failed to remove team member:', err);
        }
      },

      // Appearance actions
      loadAppearanceSettings: async () => {
        try {
          const result = await api.getAppearanceSettings();

          if (result.success && result.data) {
            set((state) => {
              state.appearanceSettings = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load appearance settings:', err);
        }
      },

      updateTheme: async (theme: Theme) => {
        try {
          const result = await api.updateAppearanceSettings({ theme });

          if (result.success) {
            set((state) => {
              state.appearanceSettings.theme = theme;
            });
          }
        } catch (err) {
          console.error('Failed to update theme:', err);
        }
      },

      updateLayoutDensity: async (density: LayoutDensity) => {
        try {
          const result = await api.updateAppearanceSettings({ layoutDensity: density });

          if (result.success) {
            set((state) => {
              state.appearanceSettings.layoutDensity = density;
            });
          }
        } catch (err) {
          console.error('Failed to update layout density:', err);
        }
      },

      updateSidebarBehavior: async (behavior: SidebarBehavior) => {
        try {
          const result = await api.updateAppearanceSettings({ sidebarBehavior: behavior });

          if (result.success) {
            set((state) => {
              state.appearanceSettings.sidebarBehavior = behavior;
            });
          }
        } catch (err) {
          console.error('Failed to update sidebar behavior:', err);
        }
      },

      // Notification actions
      loadNotificationPreferences: async () => {
        try {
          const result = await api.getNotificationPreferences();

          if (result.success && result.data) {
            set((state) => {
              state.notificationPreferences = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load notification preferences:', err);
        }
      },

      updateNotificationPreferences: async (updates: Partial<NotificationPreferences>) => {
        try {
          const result = await api.updateNotificationPreferences(updates);

          if (result.success) {
            set((state) => {
              state.notificationPreferences = {
                ...state.notificationPreferences,
                ...updates,
              };
            });
          }
        } catch (err) {
          console.error('Failed to update notification preferences:', err);
        }
      },

      // Integration actions
      loadIntegrations: async () => {
        try {
          const [emailResult, calendarResult, storageResult] = await Promise.all([
            api.listEmailIntegrations(),
            api.listCalendarIntegrations(),
            api.listStorageIntegrations(),
          ]);

          set((state) => {
            if (emailResult.success && emailResult.data) {
              state.emailIntegrations = emailResult.data;
            }
            if (calendarResult.success && calendarResult.data) {
              state.calendarIntegrations = calendarResult.data;
            }
            if (storageResult.success && storageResult.data) {
              state.storageIntegrations = storageResult.data;
            }
          });
        } catch (err) {
          console.error('Failed to load integrations:', err);
        }
      },

      connectEmailIntegration: async (provider, email) => {
        try {
          const result = await api.connectEmailIntegration(provider, email);

          if (result.success && result.data) {
            set((state) => {
              state.emailIntegrations.push(result.data!);
            });
            return result.data;
          }
          return null;
        } catch (err) {
          console.error('Failed to connect email integration:', err);
          return null;
        }
      },

      disconnectEmailIntegration: async (id: string) => {
        try {
          const result = await api.disconnectEmailIntegration(id);

          if (result.success) {
            set((state) => {
              state.emailIntegrations = state.emailIntegrations.filter((i) => i.id !== id);
            });
          }
        } catch (err) {
          console.error('Failed to disconnect email integration:', err);
        }
      },

      connectCalendarIntegration: async (provider, email) => {
        try {
          const result = await api.connectCalendarIntegration(provider, email);

          if (result.success && result.data) {
            set((state) => {
              state.calendarIntegrations.push(result.data!);
            });
            return result.data;
          }
          return null;
        } catch (err) {
          console.error('Failed to connect calendar integration:', err);
          return null;
        }
      },

      disconnectCalendarIntegration: async (id: string) => {
        try {
          const result = await api.disconnectCalendarIntegration(id);

          if (result.success) {
            set((state) => {
              state.calendarIntegrations = state.calendarIntegrations.filter(
                (i) => i.id !== id
              );
            });
          }
        } catch (err) {
          console.error('Failed to disconnect calendar integration:', err);
        }
      },

      connectStorageIntegration: async (provider, email) => {
        try {
          const result = await api.connectStorageIntegration(provider, email);

          if (result.success && result.data) {
            set((state) => {
              state.storageIntegrations.push(result.data!);
            });
            return result.data;
          }
          return null;
        } catch (err) {
          console.error('Failed to connect storage integration:', err);
          return null;
        }
      },

      disconnectStorageIntegration: async (id: string) => {
        try {
          const result = await api.disconnectStorageIntegration(id);

          if (result.success) {
            set((state) => {
              state.storageIntegrations = state.storageIntegrations.filter(
                (i) => i.id !== id
              );
            });
          }
        } catch (err) {
          console.error('Failed to disconnect storage integration:', err);
        }
      },

      // Agency actions
      loadAgencyConfig: async () => {
        try {
          const result = await api.getAgencyConfiguration();

          if (result.success && result.data) {
            set((state) => {
              state.agencyConfig = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load agency config:', err);
        }
      },

      updateAgencyConfig: async (updates: Partial<AgencyConfiguration>) => {
        try {
          const result = await api.updateAgencyConfiguration(updates);

          if (result.success) {
            await get().loadAgencyConfig(); // Reload to get auto-adjusted limits
          }
        } catch (err) {
          console.error('Failed to update agency config:', err);
        }
      },

      // AI actions
      loadAISettings: async () => {
        try {
          const result = await api.getAISettings();

          if (result.success && result.data) {
            set((state) => {
              state.aiSettings = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load AI settings:', err);
        }
      },

      updateAISettings: async (updates: Partial<AISettings>) => {
        try {
          const result = await api.updateAISettings(updates);

          if (result.success) {
            set((state) => {
              if (state.aiSettings) {
                state.aiSettings = { ...state.aiSettings, ...updates };
              }
            });
          }
        } catch (err) {
          console.error('Failed to update AI settings:', err);
        }
      },

      // Security actions
      loadSecuritySettings: async () => {
        try {
          const result = await api.getSecuritySettings();

          if (result.success && result.data) {
            set((state) => {
              state.securitySettings = result.data!;
            });
          }
        } catch (err) {
          console.error('Failed to load security settings:', err);
        }
      },

      updateSecuritySettings: async (updates: Partial<SecuritySettings>) => {
        try {
          const result = await api.updateSecuritySettings(updates);

          if (result.success) {
            set((state) => {
              if (state.securitySettings) {
                state.securitySettings = { ...state.securitySettings, ...updates };
              }
            });
          }
        } catch (err) {
          console.error('Failed to update security settings:', err);
        }
      },

      // Utility actions
      loadAllSettings: async () => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          await Promise.all([
            get().loadProfile(),
            get().loadTeamMembers(),
            get().loadAppearanceSettings(),
            get().loadNotificationPreferences(),
            get().loadIntegrations(),
            get().loadAgencyConfig(),
            get().loadAISettings(),
            get().loadSecuritySettings(),
          ]);

          set((state) => {
            state.loading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Unknown error loading settings';
            state.loading = false;
          });
        }
      },

      clearAllData: () => {
        api.clearAllData();
        set((state) => {
          state.teamMembers = [];
          state.emailIntegrations = [];
          state.calendarIntegrations = [];
          state.storageIntegrations = [];
        });
      },

      seedSampleData: async () => {
        set((state) => {
          state.loading = true;
          state.error = null;
        });

        try {
          await api.seedSampleData();
          await get().loadAllSettings();

          set((state) => {
            state.loading = false;
          });
        } catch (err) {
          set((state) => {
            state.error = err instanceof Error ? err.message : 'Unknown error seeding data';
            state.loading = false;
          });
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    })),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        appearanceSettings: state.appearanceSettings,
        notificationPreferences: state.notificationPreferences,
      }),
    }
  )
);

// Computed selectors
export const useCurrentTheme = () => {
  return useSettingsStore((state) => state.appearanceSettings.theme);
};

export const useActiveTeamMembers = () => {
  return useSettingsStore((state) => state.teamMembers.filter((m) => m.isActive));
};

export const useConnectedEmailIntegrations = () => {
  return useSettingsStore((state) =>
    state.emailIntegrations.filter((i) => i.status === 'connected')
  );
};

export const useConnectedCalendarIntegrations = () => {
  return useSettingsStore((state) =>
    state.calendarIntegrations.filter((i) => i.status === 'connected')
  );
};
